import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { CloudOff, RefreshCw, TriangleAlert, Check } from 'lucide-react';
import { onNetworkStatus, type NetworkStatus } from '../services/networkMonitor';

const DISMISS_AFTER_MS = 4000;

/**
 * Transient banner for connection and sync events.
 *
 * Announced politely to assistive tech and given an icon as well as colour, since
 * these are read one-handed in poor light where colour alone is unreliable.
 */
export function SyncToast() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<NetworkStatus | null>(null);

  useEffect(() => onNetworkStatus(setStatus), []);

  useEffect(() => {
    // Offline persists: it is a standing condition, not an event to acknowledge.
    if (!status || status.kind === 'offline') return;

    const timer = setTimeout(() => setStatus(null), DISMISS_AFTER_MS);
    return () => clearTimeout(timer);
  }, [status]);

  if (!status) return null;

  const { icon, text, tone } = describe(status, t);

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="sync-toast"
      className={`fixed left-[max(0.75rem,var(--safe-left))] right-[max(0.75rem,var(--safe-right))] top-[max(0.75rem,var(--safe-top))] z-50 flex items-center gap-2 rounded-lg px-3 py-2 text-sm shadow-lg ${tone}`}
    >
      {icon}
      <span>{text}</span>
    </div>
  );
}

function describe(status: NetworkStatus, t: TFunction) {
  switch (status.kind) {
    case 'offline':
      return {
        icon: <CloudOff className="size-4 shrink-0" aria-hidden="true" />,
        text: t('sync.toastOffline'),
        tone: 'bg-slate-800 text-slate-100 ring-1 ring-slate-600',
      };
    case 'syncing':
      return {
        icon: <RefreshCw className="size-4 shrink-0 animate-spin" aria-hidden="true" />,
        text: t('sync.toastSyncing', { count: status.pending }),
        tone: 'bg-sky-900 text-sky-50 ring-1 ring-sky-600',
      };
    case 'synced':
      return {
        icon: <Check className="size-4 shrink-0" aria-hidden="true" />,
        text: t('sync.toastSynced', { count: status.sent }),
        tone: 'bg-emerald-900 text-emerald-50 ring-1 ring-emerald-600',
      };
    case 'sync-failed':
      return {
        icon: <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />,
        text: t('sync.toastFailed', { count: status.failed }),
        tone: 'bg-red-900 text-red-50 ring-1 ring-red-600',
      };
  }
}
